import {
  Button,
  Card,
  Container,
  Divider,
  RingProgress,
  Text,
  useMantineTheme,
  Group,
  ActionIcon,
  Menu,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { getAuth } from "firebase/auth";
import React from "react";
import { HiDatabase, HiDotsVertical, HiServer, HiUser } from "react-icons/hi";
import { HiBolt, HiPencil } from "react-icons/hi2";
import useGlobalState, { globalProfile } from "../../globalState";
import { Plan } from "../../model/Plan";
import { formatBytes } from "../../utils/Utils";
import { useAppSelector } from "../../app/hooks";
import { profileSlice } from "../../state/profile/profileSlice";
import { useComputedColorScheme } from '@mantine/core';
import AppCard from "../common/AppCard";

const plans = [
  {
    id: Plan.FREE,
    name: "Free",
    price: "0 $",
    features: [
      {
        name: "Storage",
        description: "1 GB",
      },
      {
        name: "Share limits",
        description: "5 users",
      },
    ],
  },
  {
    id: Plan.PREMIUM,
    name: "Pro",
    price: "9.99 $",
    features: [
      {
        name: "Storage",
        description: "100 GB",
      },
      {
        name: "Share limits",
        description: "Unlimited",
      },
    ],
  },
];

export default function Account(props: { demo?: boolean }) {
  //const [profile, setProfile] = useGlobalState<User>(globalProfile);
  const profileState = useAppSelector((state) => state.profile);

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const colorScheme = useComputedColorScheme();

  const demoStorageTotalGb = 100;
  const demoStorageUsedGb = 40;

  const getStorageUsedPercentage = () => {
    if (props.demo) {
      return (demoStorageUsedGb / demoStorageTotalGb) * 100;
    }
    return (
      (profileState.profile?.storageStatus.used /
        profileState.profile?.storageStatus.total) *
      100
    );
  };

  const getStorageTotal = () => {
    if (props.demo) {
      return `${demoStorageTotalGb} GB`;
    }

    return formatBytes(profileState.profile?.storageStatus.total);
  };

  const getStorageUsed = () => {
    if (props.demo) {
      return `${demoStorageUsedGb} GB`;
    }

    return formatBytes(profileState.profile?.storageStatus.used);
  };

  const getStorageRemaining = () => {
    if (props.demo) {
      return `${demoStorageTotalGb - demoStorageUsedGb} GB`;
    }

    const remaining = profileState.profile?.storageStatus.total - profileState.profile?.storageStatus.used;

    return formatBytes(remaining);
  };

  const getStorageUsedGraphColor = () => {
    const usedPercentage = getStorageUsedPercentage();
    if (usedPercentage > 80) {
      return "red";
    } else if (usedPercentage > 60) {
      return "yellow";
    } else {
      return "blue";
    }
  };

  const renderUpgradeButton = (plan: Plan) => {
    /*if (profileState.profile?.plan === plan) {
      return <></>;
    }*/

    return (
      <Button className="mt-6" leftSection={<HiBolt />}>
        Upgrade
      </Button>
    );
  };

  const renderPlansCard = () => {
    return (
      <AppCard>
        <Card.Section withBorder inheritPadding py="xs">
          <Group justify="space-between">
            <Group className="flex flex-row justify-start items-center gap-2">
              <HiBolt />
              <Text fw={500}>Billing</Text>
            </Group>
          </Group>
        </Card.Section>

        <Card.Section inheritPadding py="md">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <Text c="dimmed" size="sm">
                Current Plan
              </Text>
              <Text size="lg">
                {profileState.profile?.planId === Plan.PREMIUM
                  ? "Premium"
                  : "Free"}
              </Text>
            </div>

            <div className="flex flex-col xs:flex-row justify-between items-start gap-4">
              {plans.map((plan) => (
                <Card
                  key={plan.id}
                  className="w-full"
                  withBorder
                  style={{
                    borderColor:
                      plan.id === profileState.profile?.planId
                        ? "var(--mantine-color-blue-4)"
                        : "var(--mantine-color-dark-4)",
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <Text fz="lg" fw={500}>
                      {plan.name}
                    </Text>
                    <Text fz="lg" c="dimmed">
                      {plan.price} / month
                    </Text>
                  </div>

                  <Divider className="my-4" />

                  <div className="flex flex-col gap-4">
                    {plan.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex flex-row justify-between items-center"
                      >
                        <Text c="dimmed" size="sm">
                          {feature.name}
                        </Text>
                        <Text>{feature.description}</Text>
                      </div>
                    ))}
                  </div>

                  {plan.id !== profileState.profile?.planId && (
                    <Button className="mt-6" leftSection={<HiBolt />}>
                      Upgrade to {plan.name}
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </Card.Section>
      </AppCard>
    );
  };

  const renderStorageCard = () => {
    return (
      <AppCard>
        <Card.Section withBorder inheritPadding py="xs">
          <Group justify="space-between">
            <Group className="flex flex-row justify-start items-center gap-2">
              <HiDatabase />
              <Text fw={500}>Storage usage</Text>
            </Group>
          </Group>
        </Card.Section>

        <Card.Section inheritPadding py="md">
          <div className="flex flex-row flex-wrap items-center justify-center gap-8">
            <div className="flex flex-col items-center justify-center w-fit">
              <RingProgress
                className="w-min"
                size={120}
                thickness={8}
                roundCaps={true}
                sections={[
                  {
                    value: getStorageUsedPercentage(),
                    color: getStorageUsedGraphColor(),
                  },
                ]}
                label={
                  <Text
                    c={getStorageUsedGraphColor()}
                    fw={700}
                    ta="center"
                    size="xl"
                  >
                    {getStorageUsedPercentage().toFixed(0)}%
                  </Text>
                }
              />
            </div>

            <div className="flex flex-col items-start justify-start sm:border-row gap-3 min-w-[8rem]">
              <div className="flex flex-col">
                <Text c="dimmed" size="sm" fw={500}>
                  Total
                </Text>
                <Text size="lg" fw={500}>{getStorageTotal()}</Text>
              </div>

              <div className="flex flex-col">
                <Text c="dimmed" size="sm" fw={500}>
                  Used
                </Text>
                <Text size="lg" fw={500}>{getStorageUsed()}</Text>
              </div>

              <div className="flex flex-col">
                <Text c="dimmed" size="sm" fw={500}>
                  Remaining
                </Text>
                <Text size="lg" fw={500}>{getStorageRemaining()}</Text>
              </div>
            </div>
          </div>
        </Card.Section>
      </AppCard>
    );
  };

  const renderAccountDetailsCard = () => {
    return (
      <AppCard>
        <Card.Section withBorder inheritPadding py="xs">
          <Group justify="space-between">
            <Group className="flex flex-row justify-start items-center gap-2">
              <HiUser />
              <Text fw={500}>Account details</Text>
            </Group>

            {/*<Menu withinPortal position="bottom-end" shadow="sm">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray">
                  <HiDotsVertical size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item leftSection={<HiPencil size={14} />}>
                  Edit profile
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>*/}
          </Group>
        </Card.Section>

        <Card.Section inheritPadding py="md">
          <div className="flex flex-row justify-start items-center gap-2 p-2 select-none flex-wrap">
            {getPhotoUrl() ? (
              <img
                className="h-7 w-7 min-h-7 min-w-7 m-auto rounded-full overflow-hidden"
                src={getPhotoUrl()}
                alt=""
              />
            ) : (
              <HiUser className="h-5 w-5"></HiUser>
            )}
            <Container className="flex flex-1 flex-col p-0">
              <Text fz="lg" fw={500}>
                {getDisplayName()}
              </Text>
              <Text mt={2} fz="md" c="dimmed">
                {getEmail()}
              </Text>
            </Container>
          </div>
        </Card.Section>
      </AppCard>
    );
  };

  const getEmail = () => {
    if (props.demo) {
      if (!profileState.profile) return "";
      return profileState.profile.email;
    }

    return getAuth().currentUser?.email;
  };

  const getDisplayName = () => {
    if (props.demo) {
      if (!profileState.profile) return "";
      return profileState.profile.name;
    }

    return getAuth().currentUser?.displayName;
  };

  const getPhotoUrl = () => {
    if (props.demo) {
      return "https://picsum.photos/id/10/200/300";
    }

    return getAuth().currentUser?.photoURL;
  };

  return (
    <Container
      className="w-full mx-auto flex flex-col gap-4"
      p={isMobile ? "md" : "xl"}
      maw={"40rem"}
    >
      <Group justify="space-between" align="center" mb="0">
        <Text fz="xl" className="title" fw={500}>
          Account
        </Text>
      </Group>

      {renderAccountDetailsCard()}

      {renderStorageCard()}

      {/*renderPlansCard()*/}
    </Container>
  );
}
